import { Chip } from '@progress/kendo-react-buttons';
import { useAppDispatch } from '../../hooks/redux';
import { TagEditorModel, TagObject, Tags } from '../../models/tag.model';
import { TAG_TOOLTIP_MAP } from './tags-constants';
import {
  setTagEditorObjectId,
  setTagEditorObjectType,
  setTagEditorTags,
  setTagEditorVisible,
  updateTagsInRepository,
} from '../../store/slices/tag-editor.slice';
import { useCallback } from 'react';
import { useDeleteTagMutation } from '../../store/api/tags.api';
import { Tooltip } from 'react-tooltip';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';

const convertTagsToArray = (tags: Tags): TagObject[] => {
  const tagKeys = Object.keys(tags);

  if (tagKeys.length > 0) {
    return tagKeys
      .map((key) => {
        const newKey = key.split('_')[0];

        return {
          type: newKey,
          value: tags[key],
          tooltip: `${newKey.replace(/([A-Z])/g, ' $1').trim()}: ${tags[key]}`,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  return [];
};

export function TagEditorList({ id, objectType, tags }: Readonly<TagEditorModel>) {
  const dispatch = useAppDispatch();
  const [deleteTag] = useDeleteTagMutation();

  const toggleTagEditor = useCallback(() => {
    dispatch(setTagEditorVisible(true));
    dispatch(setTagEditorObjectId(id));
    dispatch(setTagEditorObjectType(objectType));
    dispatch(setTagEditorTags([]));
  }, [dispatch, objectType, id]);
  const tagsSplit = Object.keys(tags ?? {}).reduce<{
    system: Record<string, string>;
    generic: Record<string, string>;
  }>(
    (acc, tagKey) => {
      if (tagKey.includes('_')) {
        acc.system[tagKey] = tags[tagKey];
      } else {
        acc.generic[tagKey] = tags[tagKey];
      }

      return acc;
    },
    { system: {}, generic: {} }
  );

  const removeGenericTag = useCallback(
    async (tagId: number) => {
      await deleteTag({
        tagId,
        objectId: id,
      });

      const result: any = {};
      Object.keys(tags).forEach((tagKey) => {
        if (tagKey !== tagId.toString()) {
          result[tagKey] = tags[tagKey];
        }
      });
      dispatch(
        updateTagsInRepository({
          ids: [id],
          tags: result,
          repository: objectType,
        })
      );
    },
    [deleteTag, id, dispatch, tags, objectType]
  );

  const systemTagObjects = convertTagsToArray(tagsSplit.system);
  const genericTags = tagsSplit.generic;

  const convertTagTooltip = (tagTooltip: string) => {
    const [tagKey, tagValue] = tagTooltip.split(':');
    const newKey = TAG_TOOLTIP_MAP[tagKey] ?? tagKey;

    return `${newKey}: ${tagValue}`;
  };

  return (
    <div className="chip-list chip-list-sm">
      {systemTagObjects.map((tag, index) => (
        <Chip
          className={`tag-${tag.type}`}
          key={`${tag.type}_${tag.value}_${index}`}
          size={'small'}
          fillMode={'solid'}
        >
          <span data-tooltip-id={`tag-${tag.type}-${tag.value}`}>{tag.value}</span>

          <Tooltip
            id={`tag-${tag.type}-${tag.value}-${index}`}
            place="top"
            style={{ fontSize: '0.8rem', maxWidth: 300, zIndex: 9999 }}
          >
            {convertTagTooltip(tag.tooltip)}
          </Tooltip>
        </Chip>
      ))}
      {Object.keys(genericTags).map((genericTagKey, index) => (
        <Chip
          className="system-tag tag-user"
          onRemove={() => removeGenericTag(Number(genericTagKey))}
          key={`${genericTags[genericTagKey]}_${index}`}
          size={'small'}
          fillMode={'solid'}
          removable={true}
        >
          {genericTags[genericTagKey]}
        </Chip>
      ))}
      <Chip onClick={toggleTagEditor} className="tag-editor-add-new">
        <KwelloSvgIcon icon={KWELLO_ICON.PLUS_CIRCLE} />
      </Chip>
    </div>
  );
}
